import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Edit3,
  Plus,
  Save,
  Trash2,
  X,
  BarChart3,
  StickyNote,
  ArrowRight,
} from "lucide-react";
import {
  GetDropdownData,
  GetGlobalSpecialPreferencePublishedMatches,
  GetSpecialPreferenceUsers,
  GetUserPublishedSpecialPreferenceInsight,
  GetUserSpecialPreferenceActivity,
  SearchCities,
  SearchStates,
} from "./apis/get";
import { DeleteSpecialPreferenceNotes, PostBackfillSpecialPreferencesFromInstructions, PostSpecialPreferenceNotes } from "./apis/post";
import toast from "react-hot-toast";

type SpecialPreferenceUser = {
  user_id: string;
  name: string;
  gender?: string | null;
  age?: number | null;
  city?: string | null;
  state?: string | null;
  occupation?: string | null;
  qualification?: string | null;
  community?: string | null;
  marital_status?: string | null;
  manglik?: string | null;
  income?: string | null;
  managed_by?: string | null;
  progress: number;
  is_verified: boolean;
  is_active: boolean;
  is_hold: boolean;
  assigned_to?: string | null;
  special_preference_notes?: Record<string, unknown> | null;
  preference_updated_at?: string | null;
  active_skipped_count: number;
  active_queue_count: number;
  published_match_count: number;
  photo?: string | null;
  instructions?: unknown;
  instructions_updated_at?: string | null;
  awaiting_followup?: unknown;
};

const LIMIT = 25;
type PreferenceSection = "includes" | "excludes";
type PreferenceEditorRow = { id: string; section: PreferenceSection; key: string; values: string };

const SECTION_STYLES: Record<PreferenceSection, string> = {
  includes: "border-emerald-500/20 bg-emerald-500/10 text-emerald-100",
  excludes: "border-rose-500/20 bg-rose-500/10 text-rose-100",
};
const PREFERENCE_KEYS = [
  ["country", "Country"],
  ["location", "City"], ["state", "State"], ["age", "Age"],
  ["qualification", "Qualification"], ["profession", "Profession"],
  ["height", "Height"], ["dietary", "Dietary"], ["family_type", "Family Type"],
  ["family_background", "Family Background"], ["marital_status", "Marital Status"],
  ["working_with", "Working With"], ["relocation_willingness", "Relocation Willingness"],
  ["hobbies", "Hobbies"], ["manglik", "Manglik"], ["caste", "Caste"],
  ["sub_caste", "Sub Caste"], ["gotra", "Gotra"],
  ["languages_known", "Languages Known"], ["salary", "Salary"],
  ["proximity_range", "Proximity Range"],
  ["working_location", "Working Location"],
] as const;
const DROPDOWN_KEY_MAP: Record<string, string[]> = {
  qualification: ["qualification"], profession: ["profession", "occupation"],
  dietary: ["dietaryoptions"], family_type: ["familytypeoptions"],
  family_background: ["familybackgroundoptions"], marital_status: ["profile_marital_option"],
  working_with: ["workingwithoptions"], hobbies: ["hobbies"], caste: ["caste"],
  sub_caste: ["subcaste"], gotra: ["gotra"], languages_known: ["language"],
  manglik: ["manglikoptions"],
  relocation_willingness: ["relocationoptions", "relocation_willingness"],
  height: ["heightoptions", "height"],
};

const flattenNotes = (notes?: Record<string, unknown> | null) => {
  if (!notes || typeof notes !== "object") return [];
  const output: { section: string; key: string; value: string }[] = [];
  Object.entries(notes).forEach(([section, sectionValue]) => {
    if (sectionValue && typeof sectionValue === "object" && !Array.isArray(sectionValue)) {
      Object.entries(sectionValue as Record<string, unknown>).forEach(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];
        values.filter(Boolean).forEach((item) =>
          output.push({ section: section === "requires" ? "includes" : section, key, value: String(item) }),
        );
      });
      return;
    }
    const values = Array.isArray(sectionValue) ? sectionValue : [sectionValue];
    values.filter(Boolean).forEach((item) =>
      output.push({ section: "preference", key: section, value: String(item) }),
    );
  });
  return output;
};

const formatDate = (value?: string | null) => {
  if (!value) return "--";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const parseStoredValue = (value: unknown): any => {
  if (!value) return [];
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
};

const normalizeUserNotes = (user: SpecialPreferenceUser | null) => {
  if (!user) return [];
  const values = [parseStoredValue(user.instructions), parseStoredValue(user.awaiting_followup)];
  const notes: { text: string; date?: string; type: string }[] = [];
  values.forEach((value, valueIndex) => {
    const entries = Array.isArray(value) ? value : value ? [value] : [];
    entries.forEach((entry) => {
      if (typeof entry === "string") {
        notes.push({ text: entry, type: valueIndex === 0 ? "Instruction" : "Follow-up" });
      } else if (entry && typeof entry === "object") {
        const text = entry.instruction || entry.note || entry.message || entry.text || entry.reason;
        if (text) notes.push({
          text: String(text),
          date: entry.date || entry.created_at || entry.updated_at,
          type: valueIndex === 0 ? "Instruction" : "Follow-up",
        });
      }
    });
  });
  return notes;
};

export default function SpecialPreferenceUsers() {
  const [users, setUsers] = useState<SpecialPreferenceUser[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingUser, setEditingUser] = useState<SpecialPreferenceUser | null>(null);
  const [editorRows, setEditorRows] = useState<PreferenceEditorRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [notesUser, setNotesUser] = useState<SpecialPreferenceUser | null>(null);
  const [insightUser, setInsightUser] = useState<SpecialPreferenceUser | null>(null);
  const [insightData, setInsightData] = useState<any>(null);
  const [publishedInsightData, setPublishedInsightData] = useState<any>(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [dropdownData, setDropdownData] = useState<Record<string, any>>({});
  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [stateSuggestions, setStateSuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [hoveredPhotoUser, setHoveredPhotoUser] = useState<SpecialPreferenceUser | null>(null);
  const [backfillUser, setBackfillUser] = useState<SpecialPreferenceUser | null>(null);
  const [backfillResult, setBackfillResult] = useState<any>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillScope, setBackfillScope] = useState<"single" | "all">("single");
  const [backfillUserId, setBackfillUserId] = useState("");
  const [backfillLimit, setBackfillLimit] = useState("20");
  const [backfillActiveOnly, setBackfillActiveOnly] = useState(true);
  const [backfillPanelOpen, setBackfillPanelOpen] = useState(false);
  const [globalMatchStatus, setGlobalMatchStatus] = useState<"all" | "met" | "not_met" | null>(null);
  const [globalMatchData, setGlobalMatchData] = useState<any>(null);
  const [globalMatchLoading, setGlobalMatchLoading] = useState(false);
  const [globalMatchOffset, setGlobalMatchOffset] = useState(0);
  const [globalMatchTotals, setGlobalMatchTotals] = useState<any>(null);

  useEffect(() => {
    GetGlobalSpecialPreferencePublishedMatches({ status: "all", limit: 1, offset: 0 })
      .then((response: any) => {
        if (response?.status === true) setGlobalMatchTotals(response.data?.totals || null);
      })
      .catch(() => undefined);
  }, []);

  const loadGlobalMatches = async (status: "all" | "met" | "not_met", loadMore = false) => {
    const nextOffset = loadMore ? globalMatchOffset + 20 : 0;
    setGlobalMatchStatus(status);
    if (!loadMore) setGlobalMatchData(null);
    setGlobalMatchLoading(true);
    try {
      const response = await GetGlobalSpecialPreferencePublishedMatches({ status, limit: 20, offset: nextOffset });
      if (response?.status !== true) throw new Error(response?.message || "Unable to load matches");
      const incoming = response.data || {};
      setGlobalMatchTotals(incoming.totals || null);
      setGlobalMatchData((current: any) => loadMore && current ? {
        ...incoming,
        matches: [...(current.matches || []), ...(incoming.matches || [])],
      } : incoming);
      setGlobalMatchOffset(nextOffset);
    } catch (matchError: any) {
      toast.error(matchError?.message || "Unable to load preference matches");
      if (!loadMore) setGlobalMatchData(null);
    } finally {
      setGlobalMatchLoading(false);
    }
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await GetSpecialPreferenceUsers({
        search,
        activeOnly,
        limit: LIMIT,
        offset,
      });
      if (response?.status !== true) {
        throw new Error(response?.message || response?.detail || "Unable to load users");
      }
      setUsers(Array.isArray(response.users) ? response.users : []);
      setTotal(Number(response.count || 0));
    } catch (loadError: any) {
      setUsers([]);
      setTotal(0);
      setError(loadError?.message || "Unable to load special-preference users");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, offset, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const currentPage = Math.floor(offset / LIMIT) + 1;
  const pageCount = Math.max(1, Math.ceil(total / LIMIT));
  const summary = useMemo(() => ({
    skipped: users.reduce((sum, user) => sum + user.active_skipped_count, 0),
    queued: users.reduce((sum, user) => sum + user.active_queue_count, 0),
    published: users.reduce((sum, user) => sum + user.published_match_count, 0),
  }), [users]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const loadEditorSuggestions = async () => {
    if (Object.keys(dropdownData).length || suggestionsLoading) return;
    setSuggestionsLoading(true);
    try {
      const [dropdownResponse, cityResponse, stateResponse] = await Promise.all([
        GetDropdownData(), SearchCities("", 30), SearchStates("", 30),
      ]);
      if (dropdownResponse?.status === true || dropdownResponse?.success === true) {
        setDropdownData(dropdownResponse.data || {});
      }
      const normalizeOptions = (items: any[]) => Array.from(new Set(
        (items || []).map((item) => String(
          typeof item === "string" ? item : item?.name || item?.city || item?.state || item?.value || item?.label || "",
        ).trim()).filter(Boolean),
      ));
      if (Array.isArray(cityResponse?.data)) setCitySuggestions(normalizeOptions(cityResponse.data));
      if (Array.isArray(stateResponse?.data)) setStateSuggestions(normalizeOptions(stateResponse.data));
    } catch {
      toast.error("Some preference suggestions could not be loaded");
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const openEditor = (user: SpecialPreferenceUser) => {
    const rows = flattenNotes(user.special_preference_notes).map((note, index) => ({
      id: `${Date.now()}-${index}`,
      section: (["includes", "excludes"].includes(note.section)
        ? note.section
        : "includes") as PreferenceSection,
      key: note.key,
      value: note.value,
    }));
    const grouped = new Map<string, PreferenceEditorRow>();
    rows.forEach((row) => {
      const identity = `${row.section}:${row.key}`;
      const current = grouped.get(identity);
      if (current) current.values = `${current.values}, ${row.value}`;
      else grouped.set(identity, { ...row, values: row.value });
    });
    setEditorRows(Array.from(grouped.values()));
    setEditingUser(user);
    void loadEditorSuggestions();
  };

  const suggestionsForKey = (key: string): string[] => {
    if (key === "country") return [
      "Out of India",
      "Abroad",
      "USA",
      "Canada",
      "United Kingdom",
      "Australia",
      "UAE / Dubai",
      "Germany",
      "Singapore",
      "New Zealand",
      "Ireland",
    ];
    if (key === "location" || key === "working_location") return citySuggestions;
    if (key === "state") return stateSuggestions;
    if (key === "age") return ["above 21", "above 25", "under 30", "between 24 and 28", "between 28 and 32"];
    if (key === "salary") return ["above 500000", "above 1000000", "above 1500000", "above 2000000", "above 3000000", "between 1000000 and 2000000"];
    if (key === "proximity_range") return ["under 25", "under 50", "under 100", "under 250", "under 500"];
    const values = (DROPDOWN_KEY_MAP[key] || []).flatMap((sourceKey) =>
      Array.isArray(dropdownData?.[sourceKey]) ? dropdownData[sourceKey] : [],
    );
    return Array.from(new Set(values.map((value) => String(value).trim()).filter(Boolean))).slice(0, 30);
  };

  const appendSuggestedValue = (row: PreferenceEditorRow, value: string) => {
    const current = row.values.split(",").map((item) => item.trim()).filter(Boolean);
    if (!current.some((item) => item.toLowerCase() === value.toLowerCase())) current.push(value);
    updateEditorRow(row.id, { values: current.join(", ") });
  };

  const addEditorRow = () => setEditorRows((current) => [
    ...current,
    { id: `${Date.now()}-${Math.random()}`, section: "includes", key: "", values: "" },
  ]);

  const updateEditorRow = (id: string, changes: Partial<PreferenceEditorRow>) => {
    setEditorRows((current) => current.map((row) => row.id === id ? { ...row, ...changes } : row));
  };

  const buildNotesPayload = () => {
    const notes: Record<string, Record<string, string[]>> = {};
    editorRows.forEach((row) => {
      const key = row.key.trim().toLowerCase().replace(/\s+/g, "_");
      const values = row.values.split(",").map((value) => value.trim()).filter(Boolean);
      if (!key || values.length === 0) return;
      if (!notes[row.section]) notes[row.section] = {};
      notes[row.section][key] = Array.from(new Set([
        ...(notes[row.section][key] || []),
        ...values,
      ]));
    });
    return notes;
  };

  const savePreferences = async () => {
    if (!editingUser) return;
    const notes = buildNotesPayload();
    if (Object.keys(notes).length === 0) {
      toast.error("Add at least one preference key and value");
      return;
    }
    setSaving(true);
    try {
      const response = await PostSpecialPreferenceNotes(
        { notes, merge: false },
        editingUser.user_id,
      );
      if (response?.status !== true) throw new Error(response?.message || "Update failed");
      toast.success("Special preferences updated");
      setEditingUser(null);
      await loadUsers();
    } catch (saveError: any) {
      toast.error(saveError?.message || "Unable to update preferences");
    } finally {
      setSaving(false);
    }
  };

  const clearPreferences = async () => {
    if (!editingUser || !window.confirm(`Clear all special preferences for ${editingUser.name}?`)) return;
    setSaving(true);
    try {
      const response = await DeleteSpecialPreferenceNotes(editingUser.user_id);
      if (response?.status !== true) throw new Error(response?.message || "Clear failed");
      toast.success("Special preferences cleared");
      setEditingUser(null);
      await loadUsers();
    } catch (clearError: any) {
      toast.error(clearError?.message || "Unable to clear preferences");
    } finally {
      setSaving(false);
    }
  };

  const openMatchInsight = async (user: SpecialPreferenceUser) => {
    setInsightUser(user);
    setInsightData(null);
    setPublishedInsightData(null);
    setInsightLoading(true);
    try {
      const [activityResponse, publishedResponse] = await Promise.all([
        GetUserSpecialPreferenceActivity(user.user_id, 100, 0),
        GetUserPublishedSpecialPreferenceInsight(user.user_id, 500),
      ]);
      if (activityResponse?.status !== true) throw new Error(activityResponse?.message || "Unable to load activity");
      if (publishedResponse?.status !== true) throw new Error(publishedResponse?.message || "Unable to evaluate published matches");
      setInsightData(activityResponse.data || {});
      setPublishedInsightData(publishedResponse.data || {});
    } catch (insightError: any) {
      toast.error(insightError?.message || "Unable to load match insight");
    } finally {
      setInsightLoading(false);
    }
  };

  const runInstructionBackfill = async (dryRun: boolean, user?: SpecialPreferenceUser) => {
    const effectiveUserId = user?.user_id || (backfillScope === "single" ? backfillUserId.trim() : undefined);
    if (backfillScope === "single" && !effectiveUserId) {
      toast.error("Enter a user ID");
      return;
    }
    if (user) {
      setBackfillScope("single");
      setBackfillUserId(user.user_id);
      setBackfillUser(user);
    }
    setBackfillPanelOpen(true);
    setBackfillLoading(true);
    if (dryRun) setBackfillResult(null);
    try {
      const response = await PostBackfillSpecialPreferencesFromInstructions({
        userId: effectiveUserId,
        dryRun,
        limit: backfillScope === "all" && backfillLimit.trim() ? Number(backfillLimit) : undefined,
        activeOnly: backfillActiveOnly,
      });
      if (response?.status !== true) throw new Error(response?.message || response?.detail || "Backfill failed");
      setBackfillResult(response.data || {});
      toast.success(dryRun ? "Instruction preview ready" : "Special preferences updated from instructions");
      if (!dryRun) await loadUsers();
    } catch (backfillError: any) {
      toast.error(backfillError?.message || "Unable to process instructions");
    } finally {
      setBackfillLoading(false);
    }
  };

  const openBackfillPanel = () => {
    setBackfillUser(null);
    setBackfillResult(null);
    setBackfillScope("single");
    setBackfillUserId("");
    setBackfillPanelOpen(true);
  };

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5">
        <header className="relative overflow-hidden rounded-[2rem] border border-slate-800 bg-slate-900 p-6 shadow-2xl lg:p-8">
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-600/10 blur-3xl" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.3em] text-blue-400">
                <Sparkles size={15} /> Matchmaking Operations
              </p>
              <h1 className="mt-3 text-3xl font-black uppercase italic tracking-tight text-white sm:text-4xl">Special Preference Users</h1>
              <p className="mt-2 text-sm text-slate-400">
                Review exact includes and exclusions. Update them without leaving this page.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={openBackfillPanel} className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-500/30 bg-violet-500/10 px-5 py-3 text-xs font-black uppercase tracking-wider text-violet-300 hover:bg-violet-500/20">
                <Sparkles size={16} /> Instruction backfill
              </button>
              <button
                onClick={loadUsers}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-5 py-3 text-xs font-black uppercase tracking-wider hover:border-blue-500 disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
          </div>
        </header>

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          {[
            ["Total users", total, "text-blue-300"],
            ["Skipped on this page", summary.skipped, "text-rose-300"],
            ["Queued on this page", summary.queued, "text-amber-300"],
            ["Published on this page", summary.published, "text-emerald-300"],
            ["Preference met matches", globalMatchTotals?.met ?? "--", "text-emerald-300"],
            ["Preference not met matches", globalMatchTotals?.not_met ?? "--", "text-rose-300"],
          ].map(([label, value, color]) => (
            <div key={String(label)} className="rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-xl">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600">{label}</p>
              <p className={`mt-2 text-3xl font-black italic ${color}`}>{value}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-3 md:grid-cols-2">
          <button onClick={() => loadGlobalMatches("met")} className={`rounded-2xl border p-5 text-left transition ${globalMatchStatus === "met" ? "border-emerald-400 bg-emerald-500/15 ring-1 ring-emerald-400/40" : "border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10"}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400">All users</p>
            <div className="mt-2 flex items-end justify-between gap-3"><div><h2 className="text-xl font-black text-white">Preference Met Matches</h2><p className="mt-1 text-xs text-slate-400">See every published match that satisfies the user's special preference.</p></div><span className="text-3xl font-black text-emerald-300">{globalMatchData?.totals?.met ?? "→"}</span></div>
          </button>
          <button onClick={() => loadGlobalMatches("not_met")} className={`rounded-2xl border p-5 text-left transition ${globalMatchStatus === "not_met" ? "border-rose-400 bg-rose-500/15 ring-1 ring-rose-400/40" : "border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/10"}`}>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-rose-400">All users</p>
            <div className="mt-2 flex items-end justify-between gap-3"><div><h2 className="text-xl font-black text-white">Preference Not Met Matches</h2><p className="mt-1 text-xs text-slate-400">See every published match that does not satisfy the user's special preference.</p></div><span className="text-3xl font-black text-rose-300">{globalMatchData?.totals?.not_met ?? "→"}</span></div>
          </button>
        </section>
        <button onClick={() => loadGlobalMatches("all")} className={`w-full rounded-2xl border px-5 py-4 text-left transition ${globalMatchStatus === "all" ? "border-blue-400 bg-blue-500/15 ring-1 ring-blue-400/40" : "border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10"}`}><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400">Combined view</p><h2 className="mt-1 text-lg font-black text-white">Show Met + Not Met from the same 20-match batch</h2></div><span className="rounded-xl bg-blue-500/10 px-4 py-2 text-xs font-black uppercase text-blue-300">View first 20</span></div></button>

        {globalMatchStatus && (
          <section className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-xl">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><div><p className={`text-[10px] font-black uppercase tracking-[0.2em] ${globalMatchStatus === "met" ? "text-emerald-400" : globalMatchStatus === "not_met" ? "text-rose-400" : "text-blue-400"}`}>All users · published matches</p><h2 className="mt-2 text-2xl font-black text-white">{globalMatchStatus === "met" ? "Preference Met" : globalMatchStatus === "not_met" ? "Preference Not Met" : "Preference Met + Not Met"} Matches</h2><p className="mt-1 text-xs text-slate-500">Loaded {globalMatchData?.matches?.length || 0} matches · offset {globalMatchOffset}</p></div><button onClick={() => { setGlobalMatchStatus(null); setGlobalMatchData(null); setGlobalMatchOffset(0); }} className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-xs font-bold text-slate-300"><X size={14} className="mr-1 inline" /> Close matches</button></div>
            <div className="mb-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-blue-400">Total evaluated</p><p className="mt-2 text-3xl font-black text-blue-300">{globalMatchData?.totals?.all || 0}</p></div>
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-emerald-400">Preference met</p><p className="mt-2 text-3xl font-black text-emerald-300">{globalMatchData?.totals?.met || 0}</p></div>
              <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-rose-400">Preference not met</p><p className="mt-2 text-3xl font-black text-rose-300">{globalMatchData?.totals?.not_met || 0}</p></div>
            </div>
            {globalMatchLoading && !(globalMatchData?.matches || []).length ? <div className="p-12 text-center text-slate-400"><RefreshCw className="mx-auto mb-3 animate-spin" />Evaluating published matches...</div> : (
              <div className="grid gap-3 xl:grid-cols-2">
                {(globalMatchData?.matches || []).length ? globalMatchData.matches.map((match: any, index: number) => (
                  <article key={`${match.user_id}-${match.candidate_id}-${index}`} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-[10px] font-black uppercase text-slate-500">Requested for</p><Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(match.user_id)}`} className="font-bold text-blue-300 hover:text-blue-200">{match.user_name}</Link><span className="ml-2 font-mono text-[10px] text-slate-600">{match.user_id}</span></div><span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${match.status === "met" ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>{String(match.status).replaceAll("_", " ")}</span></div>
                    <div className="my-4 border-t border-slate-800" />
                    <p className="text-[10px] font-black uppercase text-slate-500">Candidate</p><Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(match.candidate_id)}`} className="font-bold text-white hover:text-blue-300">{match.candidate_name}</Link><span className="ml-2 font-mono text-[10px] text-slate-600">{match.candidate_id}</span>
                    <p className="mt-2 text-xs text-slate-400">{[match.candidate_gender, match.candidate_location, match.candidate_profession].filter(Boolean).join(" · ") || "Profile details unavailable"}</p>
                    {match.status === "not_met" && match.mismatch_reasons?.length ? <div className="mt-3 rounded-xl border border-rose-500/20 bg-rose-500/5 p-3"><p className="text-[10px] font-black uppercase tracking-wider text-rose-400">Why preference was not met</p><ul className="mt-2 space-y-1.5 text-sm leading-6 text-rose-100">{match.mismatch_reasons.map((reason: string, reasonIndex: number) => <li key={reasonIndex} className="flex gap-2"><span className="text-rose-400">•</span><span>{reason}</span></li>)}</ul></div> : <p className="mt-3 text-sm leading-6 text-slate-300">{match.reason}</p>}
                    {!!match.evaluated_preference_keys?.length && <div className="mt-3 flex flex-wrap gap-1.5">{match.evaluated_preference_keys.map((key: string) => <span key={key} className="rounded-lg bg-slate-800 px-2 py-1 text-[9px] font-bold uppercase text-slate-400">{key.replaceAll("_", " ")}</span>)}</div>}
                    <div className="mt-4 flex flex-wrap gap-4 text-[10px] text-slate-500"><span>Score: {match.match_score ?? "--"}</span><span>Published: {formatDate(match.published_at)}</span><span>{match.evaluation_basis === "publication_time_snapshot" ? "Publication snapshot" : "Current preference fallback"}</span></div>
                  </article>
                )) : <div className="col-span-full rounded-2xl border border-dashed border-slate-700 p-12 text-center text-sm text-slate-500">No matches found in this category.</div>}
              </div>
            )}
            {(globalMatchData?.matches?.length || 0) < (globalMatchData?.count || 0) && <div className="mt-5 flex justify-center"><button disabled={globalMatchLoading} onClick={() => loadGlobalMatches(globalMatchStatus, true)} className="inline-flex items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-6 py-3 text-xs font-black uppercase text-blue-300 hover:bg-blue-500/20 disabled:cursor-wait disabled:opacity-60">{globalMatchLoading ? <RefreshCw size={15} className="animate-spin" /> : <ChevronRight size={15} />} {globalMatchLoading ? "Loading next 20..." : "Load next 20 matches"}</button></div>}
          </section>
        )}

        <form onSubmit={submitSearch} className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/50 p-4 md:flex-row md:items-end">
          <label className="flex-1">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Search</span>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3.5 text-slate-500" />
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="User ID, name, city or state"
                className="w-full rounded-xl border border-slate-700 bg-slate-950 py-3 pl-10 pr-4 text-sm outline-none focus:border-blue-500"
              />
            </div>
          </label>
          <label className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) => { setActiveOnly(event.target.checked); setOffset(0); }}
              className="accent-blue-500"
            />
            Active and not on hold
          </label>
          <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold hover:bg-blue-500">
            <SlidersHorizontal size={16} /> Apply
          </button>
        </form>

        {error && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">{error}</div>}

        <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/40">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800 text-left text-sm">
              <thead className="bg-slate-900 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Profile</th>
                  <th className="min-w-[420px] px-4 py-3">Special preferences</th>
                  <th className="px-4 py-3">Pipeline</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading && users.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading special-preference users...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No users found.</td></tr>
                ) : users.map((user) => {
                  const notes = flattenNotes(user.special_preference_notes);
                  return (
                    <tr key={user.user_id} className="align-top hover:bg-white/[0.025]">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`h-11 w-11 overflow-hidden rounded-xl border border-slate-700 bg-slate-800 ${user.photo ? "cursor-zoom-in" : ""}`}
                            onMouseEnter={() => user.photo && setHoveredPhotoUser(user)}
                            onMouseLeave={() => setHoveredPhotoUser(null)}
                          >
                            {user.photo ? <img src={user.photo} alt={`${user.name} profile`} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center font-bold text-slate-500">{user.name?.[0] || "U"}</div>}
                          </div>
                          <div>
                            <Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(user.user_id)}`} className="font-bold text-white hover:text-blue-300">{user.name}</Link>
                            <p className="font-mono text-xs text-blue-400">{user.user_id}</p>
                            <p className="mt-1 text-xs text-slate-500">Assigned: {user.assigned_to || "Unassigned"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="min-w-[210px] px-4 py-4 text-xs text-slate-300">
                        <p>{[user.gender, user.age ? `${user.age} yrs` : null].filter(Boolean).join(" · ") || "--"}</p>
                        <p className="mt-1">{[user.city, user.state].filter(Boolean).join(", ") || "--"}</p>
                        <p className="mt-1 text-slate-500">{user.occupation || "Occupation not available"}</p>
                        <div className="mt-2 flex gap-1.5">
                          <span className={`rounded-full px-2 py-0.5 ${user.is_verified ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{user.is_verified ? "Verified" : "Unverified"}</span>
                          <span className="rounded-full bg-slate-800 px-2 py-0.5">{user.progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {notes.length ? notes.map((note, index) => (
                            <span key={`${note.section}-${note.key}-${note.value}-${index}`} className={`rounded-lg border px-2.5 py-1.5 text-xs ${SECTION_STYLES[(note.section in SECTION_STYLES ? note.section : "includes") as PreferenceSection]}`}>
                              <span className="mr-1 text-[9px] font-black uppercase opacity-60">{note.section}</span>
                              <b>{note.key.replaceAll("_", " ")}:</b> {note.value}
                            </span>
                          )) : <span className="text-xs text-slate-500">Enabled, but no structured notes available.</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <p className="text-rose-300">Skipped: <b>{user.active_skipped_count}</b></p>
                        <p className="mt-1 text-amber-300">Queue: <b>{user.active_queue_count}</b></p>
                        <p className="mt-1 text-emerald-300">Published: <b>{user.published_match_count}</b></p>
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-400">{formatDate(user.preference_updated_at)}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <button onClick={() => openEditor(user)} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-black text-white hover:bg-blue-500">
                            <Edit3 size={13} /> Edit preferences
                          </button>
                          <button onClick={() => openMatchInsight(user)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/20">
                            <BarChart3 size={13} /> Match insight
                          </button>
                          <button onClick={() => setNotesUser(user)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/20">
                            <StickyNote size={13} /> User notes
                          </button>
                          <Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(user.user_id)}`} state={{ from: "special-preference-users" }} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs font-bold text-slate-300 hover:border-blue-500">
                            View profile <ExternalLink size={13} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <footer className="flex items-center justify-between border-t border-slate-800 px-4 py-3 text-sm text-slate-400">
            <span>Page {currentPage} of {pageCount} · {total} users</span>
            <div className="flex gap-2">
              <button disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - LIMIT))} className="rounded-lg border border-slate-700 p-2 hover:border-blue-500 disabled:opacity-40"><ChevronLeft size={16} /></button>
              <button disabled={offset + LIMIT >= total || loading} onClick={() => setOffset(offset + LIMIT)} className="rounded-lg border border-slate-700 p-2 hover:border-blue-500 disabled:opacity-40"><ChevronRight size={16} /></button>
            </div>
          </footer>
        </section>
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[2rem] border border-slate-700 bg-slate-900 shadow-[0_30px_100px_rgba(0,0,0,0.8)]">
            <header className="flex items-start justify-between border-b border-slate-800 bg-slate-950/50 p-6">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-400">Preference Editor</p>
                <h2 className="mt-2 text-2xl font-black text-white">{editingUser.name}</h2>
                <p className="mt-1 font-mono text-xs text-slate-500">{editingUser.user_id}</p>
              </div>
              <button onClick={() => setEditingUser(null)} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-slate-400 hover:text-white"><X size={18} /></button>
            </header>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-5 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-emerald-200"><b>Includes</b><br />Preferred values that should be present.</div>
                <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-3 text-xs text-rose-200"><b>Excludes</b><br />Values that must reject a candidate.</div>
              </div>

              <div className="space-y-3">
                {editorRows.map((row) => (
                  <div key={row.id} className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                    <div className="grid gap-3 md:grid-cols-[150px_1fr_2fr_42px]">
                      <select value={row.section} onChange={(event) => updateEditorRow(row.id, { section: event.target.value as PreferenceSection })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500">
                        <option value="includes">Includes</option>
                        <option value="excludes">Excludes</option>
                      </select>
                      <select value={row.key} onChange={(event) => updateEditorRow(row.id, { key: event.target.value, values: "" })} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500">
                        <option value="">Select preference</option>
                        {PREFERENCE_KEYS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                      <input value={row.values} onChange={(event) => updateEditorRow(row.id, { values: event.target.value })} placeholder="Comma-separated values" className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500" />
                      <button onClick={() => setEditorRows((current) => current.filter((item) => item.id !== row.id))} className="flex items-center justify-center rounded-lg border border-rose-500/20 text-rose-400 hover:bg-rose-500/10" title="Remove row"><Trash2 size={15} /></button>
                    </div>
                    {row.key && (
                      <div className="mt-3 border-t border-slate-800 pt-3">
                        <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500"><Sparkles size={12} className="text-blue-400" /> Suggestions from admin data {suggestionsLoading && "· loading..."}</div>
                        <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                          {suggestionsForKey(row.key).length ? suggestionsForKey(row.key).map((suggestion) => {
                            const selected = row.values.split(",").some((value) => value.trim().toLowerCase() === suggestion.toLowerCase());
                            return <button type="button" key={suggestion} onClick={() => appendSuggestedValue(row, suggestion)} className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition ${selected ? "border-blue-400 bg-blue-500/20 text-blue-200" : "border-slate-700 bg-slate-900 text-slate-400 hover:border-blue-500 hover:text-white"}`}>{suggestion}</button>;
                          }) : <span className="text-xs text-slate-600">No API suggestions for this key. You can enter a custom value above.</span>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {editorRows.length === 0 && <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center text-sm text-slate-500">No preference rows. Add one below.</div>}
              </div>
              <button onClick={addEditorRow} className="mt-4 inline-flex items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-blue-300 hover:bg-blue-500/20"><Plus size={15} /> Add preference</button>
            </div>

            <footer className="flex flex-col-reverse gap-3 border-t border-slate-800 bg-slate-950/50 p-5 sm:flex-row sm:items-center sm:justify-between">
              <button disabled={saving} onClick={clearPreferences} className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-500/30 px-4 py-3 text-xs font-black uppercase text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"><Trash2 size={15} /> Clear all</button>
              <div className="flex gap-3">
                <button disabled={saving} onClick={() => setEditingUser(null)} className="rounded-xl border border-slate-700 px-5 py-3 text-xs font-black uppercase text-slate-300 disabled:opacity-50">Cancel</button>
                <button disabled={saving} onClick={savePreferences} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-xs font-black uppercase text-white hover:bg-blue-500 disabled:opacity-50"><Save size={15} /> {saving ? "Saving..." : "Save changes"}</button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {notesUser && (() => {
        const notes = normalizeUserNotes(notesUser);
        return (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md">
            <div className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-[2rem] border border-slate-700 bg-slate-900 shadow-2xl">
              <header className="flex items-start justify-between border-b border-slate-800 bg-slate-950/50 p-6">
                <div><p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400">User Notes</p><h2 className="mt-2 text-2xl font-black text-white">{notesUser.name}</h2><p className="mt-1 font-mono text-xs text-slate-500">{notesUser.user_id}</p></div>
                <button onClick={() => setNotesUser(null)} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-slate-400 hover:text-white"><X size={18} /></button>
              </header>
              <div className="flex-1 space-y-3 overflow-y-auto p-6">
                {notes.length ? notes.map((note, index) => (
                  <article key={`${note.type}-${index}`} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex items-center justify-between gap-3"><span className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${note.type === "Follow-up" ? "bg-blue-500/10 text-blue-300" : "bg-amber-500/10 text-amber-300"}`}>{note.type}</span><span className="text-[11px] text-slate-500">{formatDate(note.date)}</span></div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-200">{note.text}</p>
                  </article>
                )) : <div className="rounded-2xl border border-dashed border-slate-700 p-12 text-center text-sm text-slate-500">No user instructions or follow-up notes available.</div>}
              </div>
              <footer className="flex justify-between border-t border-slate-800 bg-slate-950/50 p-5"><span className="text-xs text-slate-500">Last updated: {formatDate(notesUser.instructions_updated_at)}</span><Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(notesUser.user_id)}`} className="inline-flex items-center gap-2 text-xs font-bold text-blue-300">Open full profile <ArrowRight size={14} /></Link></footer>
            </div>
          </div>
        );
      })()}

      {insightUser && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-slate-700 bg-slate-900 shadow-2xl">
            <header className="flex items-start justify-between border-b border-slate-800 bg-slate-950/50 p-6">
              <div><p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-400">Special Preference Match Insight</p><h2 className="mt-2 text-2xl font-black text-white">{insightUser.name}</h2><p className="mt-1 font-mono text-xs text-slate-500">{insightUser.user_id}</p></div>
              <button onClick={() => setInsightUser(null)} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-slate-400 hover:text-white"><X size={18} /></button>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
              {insightLoading ? <div className="p-16 text-center text-slate-400">Loading match insight...</div> : (
                <>
                  <section className="mb-8 rounded-3xl border border-blue-500/20 bg-blue-500/5 p-5">
                    <div className="mb-5">
                      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-blue-300">Published match compatibility</p>
                      <p className="mt-2 text-sm text-slate-400">New matches are checked against the exact preference snapshot captured when they were published. Older matches without a stored snapshot use the current preference and are clearly marked as fallback.</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {[
                        ["Published evaluated", publishedInsightData?.totals?.published_count || 0, "text-blue-300"],
                        ["Preference met", publishedInsightData?.totals?.special_preference_met_count || 0, "text-emerald-300"],
                        ["Preference not met", publishedInsightData?.totals?.special_preference_not_met_count || 0, "text-rose-300"],
                        ["Unable to evaluate", publishedInsightData?.totals?.unable_to_evaluate_count || 0, "text-amber-300"],
                      ].map(([label, value, color]) => <div key={String(label)} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-2 text-3xl font-black ${color}`}>{value}</p></div>)}
                    </div>
                    <div className="mt-5 space-y-3">
                      {(publishedInsightData?.matches || []).length ? publishedInsightData.matches.map((match: any) => (
                        <article key={match.candidate_id} className="grid gap-4 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 md:grid-cols-[1fr_190px]">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(match.candidate_id)}`} className="font-bold text-white hover:text-blue-300">{match.name || match.candidate_id}</Link>
                              <span className="font-mono text-[10px] text-slate-500">{match.candidate_id}</span>
                              <span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase ${match.status === "met" ? "bg-emerald-500/10 text-emerald-300" : match.status === "not_met" ? "bg-rose-500/10 text-rose-300" : "bg-amber-500/10 text-amber-300"}`}>{String(match.status || "unknown").replaceAll("_", " ")}</span>
                            </div>
                            <p className="mt-2 text-xs text-slate-400">{[match.gender, match.age ? `${match.age} yrs` : null, match.location, match.profession].filter(Boolean).join(" · ")}</p>
                            <p className="mt-3 text-sm leading-6 text-slate-300">{match.reason}</p>
                            <p className={`mt-2 text-[10px] font-black uppercase tracking-wider ${match.evaluation_basis === "publication_time_snapshot" ? "text-emerald-400" : "text-amber-400"}`}>{match.evaluation_basis === "publication_time_snapshot" ? `Publication snapshot · ${formatDate(match.preference_snapshot_at)}` : "Current preference fallback · legacy match"}</p>
                            {!!match.evaluated_preference_keys?.length && <div className="mt-3 flex flex-wrap gap-1.5">{match.evaluated_preference_keys.map((key: string) => <span key={key} className="rounded-lg bg-slate-800 px-2 py-1 text-[10px] font-bold uppercase text-slate-400">{key.replaceAll("_", " ")}</span>)}</div>}
                          </div>
                          <div className="text-xs text-slate-500"><p>Match score: <span className="text-slate-300">{match.match_score ?? "--"}</span></p><p className="mt-2">Source: <span className="text-slate-300">{match.source_service || "--"}</span></p><p className="mt-2">Published: <span className="text-slate-300">{formatDate(match.published_at)}</span></p></div>
                        </article>
                      )) : <div className="rounded-2xl border border-dashed border-slate-700 p-10 text-center text-sm text-slate-500">No published matches are available for evaluation.</div>}
                    </div>
                  </section>
                  <p className="mb-3 text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">Skip and queue movement</p>
                  <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      ["Currently skipped", insightData?.totals?.currently_skipped_count || 0, "text-rose-300"],
                      ["Reactivated", insightData?.totals?.reactivated_count || 0, "text-emerald-300"],
                      ["Inserted to queue", insightData?.totals?.inserted_to_queue_count || 0, "text-blue-300"],
                      ["Existing queue reused", insightData?.totals?.requeued_to_existing_queue_count || 0, "text-amber-300"],
                    ].map(([label, value, color]) => <div key={String(label)} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4"><p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-2 text-3xl font-black ${color}`}>{value}</p></div>)}
                  </div>
                  <div className="space-y-3">
                    {(insightData?.trace_records || []).length ? insightData.trace_records.map((record: any, index: number) => (
                      <article key={`${record.candidate_id}-${record.moved_at}-${index}`} className="grid gap-4 rounded-2xl border border-slate-800 bg-slate-950/50 p-4 md:grid-cols-[1fr_170px]">
                        <div><div className="flex flex-wrap items-center gap-2"><Link target="_blank" rel="noopener noreferrer" to={`/users/user/${encodeURIComponent(record.candidate_id)}`} className="font-bold text-white hover:text-blue-300">{record.name || record.candidate_id}</Link><span className="font-mono text-[10px] text-slate-500">{record.candidate_id}</span><span className={`rounded-full px-2 py-1 text-[9px] font-black uppercase ${record.special_preference_status === "currently_skipped" ? "bg-rose-500/10 text-rose-300" : "bg-emerald-500/10 text-emerald-300"}`}>{record.special_preference_status}</span></div><p className="mt-2 text-xs text-slate-400">{[record.gender, record.age ? `${record.age} yrs` : null, record.location, record.profession].filter(Boolean).join(" · ")}</p><p className="mt-3 text-sm leading-6 text-slate-300">{record.reason?.reason || record.reason || "No reason recorded"}</p></div>
                        <div className="text-xs text-slate-500"><p>Source: <span className="text-slate-300">{record.source_service || "--"}</span></p><p className="mt-2">Queue action: <span className="text-slate-300">{record.queue_action || "--"}</span></p><p className="mt-2">Moved: <span className="text-slate-300">{formatDate(record.moved_at)}</span></p></div>
                      </article>
                    )) : <div className="rounded-2xl border border-dashed border-slate-700 p-12 text-center text-sm text-slate-500">No special-preference match movement activity is available.</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {backfillPanelOpen && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-md">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[2rem] border border-violet-500/20 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-violet-400">Instruction Sync</p>
                <h2 className="mt-2 text-2xl font-black text-white">{backfillUser?.name || "Special Preference Backfill"}</h2>
                <p className="font-mono text-xs text-slate-500">{backfillUser?.user_id || "Single user or bulk scan"}</p>
              </div>
              <button onClick={() => { setBackfillPanelOpen(false); setBackfillUser(null); setBackfillResult(null); }} disabled={backfillLoading} className="rounded-xl border border-slate-700 bg-slate-950 p-3 text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            {!backfillLoading && !backfillResult && (
              <div className="mt-6 space-y-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-5">
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-900 p-1">
                  <button onClick={() => setBackfillScope("single")} className={`rounded-lg px-4 py-2.5 text-xs font-black uppercase ${backfillScope === "single" ? "bg-violet-600 text-white" : "text-slate-400"}`}>Single user</button>
                  <button onClick={() => { setBackfillScope("all"); setBackfillUser(null); }} className={`rounded-lg px-4 py-2.5 text-xs font-black uppercase ${backfillScope === "all" ? "bg-violet-600 text-white" : "text-slate-400"}`}>All users</button>
                </div>
                {backfillScope === "single" ? (
                  <label className="block"><span className="mb-2 block text-xs font-bold uppercase text-slate-500">User ID</span><input value={backfillUserId} onChange={(event) => setBackfillUserId(event.target.value)} placeholder="U123456789012" className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 font-mono text-sm outline-none focus:border-violet-500" /></label>
                ) : (
                  <label className="block"><span className="mb-2 block text-xs font-bold uppercase text-slate-500">Limit (optional)</span><input type="number" min="1" value={backfillLimit} onChange={(event) => setBackfillLimit(event.target.value)} placeholder="Leave blank for all" className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm outline-none focus:border-violet-500" /></label>
                )}
                <label className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-sm"><input type="checkbox" checked={backfillActiveOnly} onChange={(event) => setBackfillActiveOnly(event.target.checked)} className="accent-violet-500" /><span><b>Active users only</b><span className="mt-0.5 block text-xs text-slate-500">Maps to the API active_only option.</span></span></label>
                <div className="flex justify-end gap-3"><button onClick={() => setBackfillPanelOpen(false)} className="rounded-xl border border-slate-700 px-5 py-3 text-sm font-bold text-slate-300">Cancel</button><button onClick={() => runInstructionBackfill(true)} className="rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white hover:bg-violet-500">Run dry preview</button></div>
              </div>
            )}
            {backfillLoading ? (
              <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-950/60 p-10 text-center text-sm text-slate-400"><RefreshCw className="mx-auto mb-3 animate-spin" /> Processing user instructions with primary LLM...</div>
            ) : backfillResult ? (
              <div className="mt-6 space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl bg-slate-950 p-4"><p className="text-[10px] uppercase text-slate-500">Scanned</p><p className="mt-2 text-xl font-black text-violet-300">{backfillResult.scanned || 0}</p></div>
                  <div className="rounded-xl bg-slate-950 p-4"><p className="text-[10px] uppercase text-slate-500">Would update</p><p className="mt-2 text-xl font-black">{backfillResult.would_update || 0}</p></div>
                  <div className="rounded-xl bg-slate-950 p-4"><p className="text-[10px] uppercase text-slate-500">LLM result</p><p className="mt-2 text-xl font-black">{backfillResult.llm_batch_returned_users || 0}/{backfillResult.llm_batch_requested_users || 0}</p></div>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-500">Proposed special preference</p>
                  <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap text-xs leading-6 text-emerald-300">{JSON.stringify((backfillResult.results || []).filter((result: any) => result.special_preference_notes).map((result: any) => ({ user_id: result.user_id, status: result.status, special_preference_notes: result.special_preference_notes })), null, 2)}</pre>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-500">Processed instruction</p>
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-300">{(backfillResult.results || []).flatMap((result: any) => (result.processed_instruction_lines || []).map((line: string) => `${result.user_id}: ${line}`)).join("\n\n") || "No preference instruction found."}</p>
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setBackfillPanelOpen(false); setBackfillUser(null); setBackfillResult(null); }} className="rounded-xl border border-slate-700 px-5 py-3 text-sm font-bold text-slate-300">Close</button>
                  <button onClick={() => runInstructionBackfill(false)} disabled={(backfillResult.would_update || 0) === 0} className="rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40">Apply update</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {hoveredPhotoUser?.photo && (
        <div className="pointer-events-none fixed inset-0 z-[1400] flex items-center justify-center bg-slate-950/55 p-8 backdrop-blur-sm">
          <figure className="overflow-hidden rounded-3xl border border-slate-600 bg-slate-900 p-3 shadow-[0_30px_100px_rgba(0,0,0,0.75)]">
            <img
              src={hoveredPhotoUser.photo}
              alt={`${hoveredPhotoUser.name} full profile`}
              className="max-h-[78vh] max-w-[78vw] rounded-2xl object-contain"
            />
            <figcaption className="px-2 pb-1 pt-3 text-center">
              <p className="font-bold text-white">{hoveredPhotoUser.name}</p>
              <p className="mt-0.5 font-mono text-xs text-blue-400">{hoveredPhotoUser.user_id}</p>
            </figcaption>
          </figure>
        </div>
      )}
    </main>
  );
}
